import { useQuery } from '@tanstack/react-query';

import {
  getLegacySummaryByDateRange,
  getSummaryByDateRange,
  getSummaryClickedUsers,
  getSummaryDetail,
} from 'api/summary';
import { queryKeys } from 'constants/queryKeys';
import { getSendHistoryClickedUsers } from 'api/sendHistory';
import { summaryManagementStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type {
  TLegacySummaryRow,
  TSummaryClickedUsersResponse,
  TSummaryDetail,
  TSummaryMode,
  TSummaryResponse,
  TSummaryRow,
  TSummarySourceType,
} from 'types/summary';
import { toErrorMessage } from './utils';

type UseSummaryOptions = {
  mode: TSummaryMode;
  from: string;
  to: string;
  currentPage: number;
  pageSize: number;
  sourceType?: TSummarySourceType;
};

type UseSummaryResult = {
  data: TSummaryResponse<TSummaryRow | TLegacySummaryRow> | undefined;
  loading: boolean;
  isRefetching: boolean;
  error: string | undefined;
  /** 検索条件が同じでも利用者操作で取得し直す。 */
  refetch: () => void;
};

const manualRefreshQueryOptions = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

export function useSummary(options: UseSummaryOptions): UseSummaryResult {
  const { user } = useAuth();

  const query = useQuery<
    TSummaryResponse<TSummaryRow | TLegacySummaryRow>
  >({
    queryKey: [
      queryKeys.SUMMARY,
      'list',
      user?.uid,
      options.mode,
      options.from,
      options.to,
      options.currentPage,
      options.pageSize,
      options.mode === 'CLICK' ? options.sourceType ?? 'ALL' : 'BULK_ONLY',
    ],
    enabled: Boolean(user?.uid && options.from && options.to),
    queryFn: () =>
      options.mode === 'LEGACY_OPEN'
        ? getLegacySummaryByDateRange(options.from, options.to, {
            currentPage: options.currentPage,
            pageSize: options.pageSize,
          })
        : getSummaryByDateRange(options.from, options.to, {
            currentPage: options.currentPage,
            pageSize: options.pageSize,
            ...(options.sourceType ? { sourceType: options.sourceType } : {}),
          }),
    ...manualRefreshQueryOptions,
    retry: false,
  });

  return {
    data: query.data,
    loading: Boolean(user?.uid) && query.isPending,
    isRefetching: query.isRefetching,
    error: query.error
      ? toErrorMessage(query.error, summaryManagementStrings.errors.loadFailed)
      : undefined,
    refetch: () => {
      void query.refetch();
    },
  };
}

type UseSummaryDetailResult = {
  data: TSummaryDetail | undefined;
  loading: boolean;
  isRefetching: boolean;
  error: string | undefined;
  refetch: () => void;
};

type UseSummaryClickedUsersResult = {
  data: TSummaryClickedUsersResponse | undefined;
  loading: boolean;
  error: string | undefined;
};

/** クリックした利用者一覧。詳細を開いたあとモーダルを開いたときだけ取得する。 */
export function useSummaryClickedUsers(options: {
  dispatchId: string | null;
  currentPage: number;
  pageSize: number;
  enabled: boolean;
  history?: boolean;
}): UseSummaryClickedUsersResult {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: [
      options.history ? queryKeys.SEND_HISTORY : queryKeys.SUMMARY,
      'clicked-users',
      user?.uid,
      options.dispatchId,
      options.currentPage,
      options.pageSize,
    ],
    enabled: Boolean(user?.uid && options.dispatchId && options.enabled),
    queryFn: () =>
      (options.history ? getSendHistoryClickedUsers : getSummaryClickedUsers)(
        options.dispatchId!,
        { currentPage: options.currentPage, pageSize: options.pageSize },
      ),
    ...manualRefreshQueryOptions,
    retry: false,
  });

  return {
    data: query.data,
    loading:
      Boolean(user?.uid && options.dispatchId && options.enabled) &&
      query.isPending,
    error: query.error
      ? toErrorMessage(
          query.error,
          summaryManagementStrings.errors.clickedUsersLoadFailed,
        )
      : undefined,
  };
}

export function useSummaryDetail(
  dispatchId: string | null,
): UseSummaryDetailResult {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: [queryKeys.SUMMARY, 'detail', user?.uid, dispatchId],
    enabled: Boolean(user?.uid && dispatchId),
    queryFn: () => getSummaryDetail(dispatchId!),
    ...manualRefreshQueryOptions,
    retry: false,
  });

  return {
    data: query.data,
    loading: Boolean(user?.uid && dispatchId) && query.isPending,
    isRefetching: query.isRefetching,
    error: query.error
      ? toErrorMessage(
          query.error,
          summaryManagementStrings.errors.detailLoadFailed,
        )
      : undefined,
    refetch: () => {
      void query.refetch();
    },
  };
}
